import * as React from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { authKeys, fetchMe, type Me } from "@/lib/api/auth";
import { statusOf } from "@/lib/api";
import { ErrorPanel } from "@/components/common/ErrorPanel";
import { LoadingSkeleton } from "@/components/common/LoadingSkeleton";
import type { Role } from "@/lib/api/types";

/**
 * The session.
 *
 * GET /api/v1/auth/me returns the PORTAL_USERS ROW - id, email, role, revoked,
 * last_login_at - plus the live session block. The role comes from that row and
 * from nowhere else: never from a token claim, never from a cookie the browser
 * can read, never cached in localStorage. auth.Handlers.Me reads it out of the
 * request context that the guarding tier populated from the database on this
 * request, so a role change takes effect on the next call rather than on the
 * next login.
 */
export function useMe(): UseQueryResult<Me, unknown> {
  return useQuery({
    queryKey: authKeys.me(),
    queryFn: fetchMe,
    // A 401 here is the ANSWER ("there is no session"), not a failure to retry.
    retry: false,
    staleTime: 60_000,
  });
}

export const ROLE_RANK: Record<Role, number> = {
  reader: 0,
  admin: 2,
};

/** True when `role` is at least `minimum`. */
export function roleAtLeast(role: Role, minimum: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

export interface RequireAuthProps {
  /** Omit to render the router <Outlet />. */
  children?: React.ReactNode;
}

/**
 * Gate: there must be a session, and the operator must not be revoked.
 *
 * This is a CONVENIENCE, not the security boundary. Every route is guarded
 * server-side through auth.RouteGuards, and this component only decides what to
 * paint while the server decides what to serve.
 */
export function RequireAuth({ children }: RequireAuthProps): React.ReactElement {
  const location = useLocation();
  const me = useMe();

  if (me.isPending) {
    return <LoadingSkeleton variant="page" label="Checking your session" />;
  }

  if (me.isError) {
    const status = statusOf(me.error);
    if (status === 401) {
      const returnTo = `${location.pathname}${location.search}`;
      return <Navigate to={`/login?return_to=${encodeURIComponent(returnTo)}`} replace />;
    }
    return (
      <div className="p-6">
        <ErrorPanel
          action="check your session"
          error={me.error}
          onRetry={() => {
            void me.refetch();
          }}
        />
      </div>
    );
  }

  if (me.data.revoked) {
    return <Navigate to="/403" replace state={{ reason: "revoked" }} />;
  }

  return <>{children ?? <Outlet />}</>;
}
